/**
 * Hook-uri mici, reutilizate de pagini.
 *
 * `useResource` acoperă exact cele patru stări pe care le cere specificația (#38):
 * loading, empty (lista/goală), error (mesaj prietenos, cu reîncercare) și success.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api.js';
import { ROMANIA_TZ } from './romania-time.js';

export interface ResourceState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  /**
   * Actualizare locală a datelor, utile pentru modificări optimiste (ex. comutarea
   * disponibilității unui produs, care trebuie să pară instantanee în timpul serviciului).
   * Acceptă și o funcție de tip updater, ca `useState`.
   */
  setData: (value: T | null | ((current: T | null) => T | null)) => void;
}

export function useResource<T>(loader: () => Promise<T>, deps: unknown[] = []): ResourceState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const alive = useRef(true);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await loaderRef.current();
      if (alive.current) setData(result);
    } catch (err) {
      if (!alive.current) return;
      setError(err instanceof ApiError ? err.userMessage : 'Nu am putut încărca datele.');
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void run();
    return () => {
      alive.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error, reload: run, setData };
}

/* ------------------------------------------------------------------- formatări */

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('ro-RO').format(value);
}

export function formatCurrency(cents: number, currency = 'RON'): string {
  return new Intl.NumberFormat('ro-RO', { style: 'currency', currency, minimumFractionDigits: 2 }).format(cents / 100);
}

export function formatDate(value: string, options?: Intl.DateTimeFormatOptions): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('ro-RO', { timeZone: ROMANIA_TZ, ...(options ?? { day: '2-digit', month: 'short', year: 'numeric' }) }).format(date);
}

export function formatDateTime(value: string): string {
  return formatDate(value, { timeZone: ROMANIA_TZ, day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function relativeTime(value: string): string {
  const date = new Date(value).getTime();
  const diff = Date.now() - date;
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return 'chiar acum';
  if (minutes < 60) return `acum ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `acum ${hours} h`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'ieri';
  if (days < 30) return `acum ${days} zile`;
  return formatDate(value);
}

/** Etichete umane pentru acțiunile din audit (nu se afișează niciodată codul brut). */
export const AUDIT_LABELS: Record<string, string> = {
  'account.created': 'Cont creat',
  'auth.login': 'Autentificare',
  'auth.logout': 'Deconectare',
  'auth.login_failed': 'Autentificare eșuată',
  'auth.password_changed': 'Parolă schimbată',
  'auth.email_verified': 'Email confirmat',
  'team.invite_sent': 'Invitație trimisă',
  'team.invite_accepted': 'Invitație acceptată',
  'team.invite_revoked': 'Invitație revocată',
  'team.member_removed': 'Membru eliminat',
  'team.role_changed': 'Rol modificat',
  'menu.published': 'Meniu publicat',
  'product.price_changed': 'Preț modificat',
  'promotion.activated': 'Promoție activată',
  'display.paired': 'Ecran conectat',
  'display.offline': 'Ecran offline',
  'restaurant.created': 'Restaurant creat',
  'restaurant.updated': 'Restaurant actualizat',
  'location.created': 'Locație creată',
  'location.updated': 'Locație actualizată',
  'account.updated': 'Setări cont modificate',
};

export function auditLabel(action: string): string {
  return AUDIT_LABELS[action] ?? 'Activitate';
}
